import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Phone,
  Settings,
  Users,
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Shield,
  BarChart3,
  Clock,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminRingbaSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [clientConfigs, setClientConfigs] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    configuredClients: 0,
    activeClients: 0,
    totalEvents: 0,
    callsTracked: 0
  });
  const [eventFilters, setEventFilters] = useState({
    client_id: '',
    event_type: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dme_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch global settings
      const settingsRes = await axios.get(`${API_URL}/api/admin/settings/ringba`, { headers });
      setGlobalEnabled(settingsRes.data.enabled || false);
      setClientConfigs(settingsRes.data.client_configs || []);
      setStats({
        configuredClients: settingsRes.data.configured_count || 0,
        activeClients: settingsRes.data.active_count || 0,
        totalEvents: settingsRes.data.events_count || 0,
        callsTracked: settingsRes.data.calls_tracked || 0
      });

      // Fetch available clients (those without Ringba config)
      const clientsRes = await axios.get(`${API_URL}/api/admin/clients`, { headers });
      const configuredIds = (settingsRes.data.client_configs || []).map(c => c.client_id);
      const available = (clientsRes.data || []).filter(c => !configuredIds.includes(c.id));
      setAvailableClients(available);

      // Fetch recent events
      const eventsRes = await axios.get(`${API_URL}/api/admin/ringba/events`, { 
        headers,
        params: { limit: 50 }
      });
      setEvents(eventsRes.data || []);

    } catch (error) {
      console.error('Error fetching Ringba settings:', error);
      toast.error('Failed to load Ringba settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleGlobal = async (enabled) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/settings/ringba`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGlobalEnabled(enabled);
      toast.success(`Ringba ${enabled ? 'enabled' : 'disabled'} globally`);
    } catch (error) {
      toast.error('Failed to update Ringba setting');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleClient = async (clientId, enabled) => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/clients/${clientId}/ringba/toggle`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setClientConfigs(prev => 
        prev.map(c => c.client_id === clientId ? { ...c, is_active: enabled } : c)
      );
      
      toast.success(`Ringba ${enabled ? 'enabled' : 'disabled'} for client`);
    } catch (error) {
      toast.error('Failed to update client Ringba setting');
    }
  };

  const handleEnableForClient = async (clientId) => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/clients/${clientId}/ringba/toggle`,
        { enabled: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Ringba enabled for client');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to enable Ringba for client');
    }
  };

  const fetchFilteredEvents = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const params = { limit: 50 };
      if (eventFilters.client_id) params.client_id = eventFilters.client_id;
      if (eventFilters.event_type) params.event_type = eventFilters.event_type;

      const res = await axios.get(`${API_URL}/api/admin/ringba/events`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setEvents(res.data || []);
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-ringba-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6 text-orange-600" />
              Ringba Settings
            </h1>
            <p className="text-muted-foreground">Manage Ringba call tracking integration for clients</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Global Toggle Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <CardTitle>Global Ringba Setting</CardTitle>
                <CardDescription>Enable or disable Ringba for all clients platform-wide</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${globalEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                {globalEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={globalEnabled}
                onCheckedChange={handleToggleGlobal}
                disabled={saving}
                data-testid="ringba-global-toggle"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.configuredClients}</p>
                <p className="text-sm text-muted-foreground">Configured Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeClients}</p>
                <p className="text-sm text-muted-foreground">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalEvents}</p>
                <p className="text-sm text-muted-foreground">Total Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.callsTracked}</p>
                <p className="text-sm text-muted-foreground">Calls Tracked</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client Configurations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Client Ringba Configurations</CardTitle>
          <CardDescription>Manage Ringba settings for individual clients</CardDescription>
        </CardHeader>
        <CardContent>
          {clientConfigs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No clients have configured Ringba yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Account ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Connection</TableHead>
                  <TableHead>Last Test</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientConfigs.map((config) => (
                  <TableRow key={config.client_id}>
                    <TableCell className="font-medium">{config.client_name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {config.account_id || 'Not set'}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={config.is_active ? 'default' : 'secondary'}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {config.connection_tested ? (
                        config.last_test_result === 'success' ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" /> Connected
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-4 h-4" /> Failed
                          </span>
                        )
                      ) : (
                        <span className="flex items-center gap-1 text-gray-500">
                          <AlertTriangle className="w-4 h-4" /> Not tested
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(config.last_test_at)}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={config.is_active}
                        onCheckedChange={(checked) => handleToggleClient(config.client_id, checked)}
                        disabled={!globalEnabled}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enable for New Clients */}
      {availableClients.length > 0 && globalEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Enable Ringba for Clients</CardTitle>
            <CardDescription>Select clients to enable Ringba call tracking</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableClients.slice(0, 9).map((client) => (
                <div
                  key={client.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <span className="font-medium">{client.company_name}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEnableForClient(client.id)}
                  >
                    Enable
                  </Button>
                </div>
              ))}
            </div>
            {availableClients.length > 9 && (
              <p className="text-sm text-muted-foreground mt-3">
                And {availableClients.length - 9} more clients...
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Events Log */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Ringba Events Log</CardTitle>
              <CardDescription>Recent Ringba-related events and activities</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={eventFilters.event_type || "all"}
                onValueChange={(value) => setEventFilters(prev => ({ ...prev, event_type: value === "all" ? "" : value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="config_saved">Config Saved</SelectItem>
                  <SelectItem value="config_deleted">Config Deleted</SelectItem>
                  <SelectItem value="connection_test_success">Test Success</SelectItem>
                  <SelectItem value="connection_test_failed">Test Failed</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={fetchFilteredEvents}>
                Filter
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No events recorded yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 border rounded-lg text-sm"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{event.event_type}</Badge>
                    <span className="text-muted-foreground">
                      Client: {event.client_id?.slice(0, 8)}...
                    </span>
                    {event.user_email && (
                      <span className="text-muted-foreground">by {event.user_email}</span>
                    )}
                  </div>
                  <span className="text-muted-foreground">{formatDate(event.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
